import React, {
  createContext,
  useState,
  useEffect,
  useContext,
  ReactNode,
} from "react";

import logo from "./components/assets/COFIT_LOGO-removebg-preview.png";

/* ------------------ Types ------------------ */

type ThemeName = "lightBlue" | "lightOrange" | "dark";

type ThemeStyles = {
  [key: string]: string;
};

type ThemesObject = Record<ThemeName, ThemeStyles>;

interface ThemeContextType {
  theme: ThemeName;
  handleThemeChange: (selectedTheme: ThemeName) => void;
  applyBackendTheme: (backendTheme: ThemeName) => void;
}

/* ------------------ Context ------------------ */

const ThemeContext = createContext<ThemeContextType | null>(null);

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme must be used inside ThemeProvider");
  }
  return ctx;
};

/* ------------------ Provider ------------------ */

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const themes: ThemesObject = {
    lightBlue: {
      "--background-color": "#5dece7",
      "--button-color": "#2b4a6f",
      "--svg-color": "#00324d",
      "--sidebar-color": "#ffffff",
      "--sidebar-hover-color": "#5dece7",
      "--component-background-color": "#ffffff",
      "--authbox-color": "#ffffff",
      "--menu-item-color": "#00324d",
      "--text-color": "#002535",
      "--topbar-background": "linear-gradient(to right, #d7ffff, #a8f6f1, #5dece7)",
      "--card-background": "#ffffff",
      "--logo-background-image": `url(${logo})`,
      "--line-color": "#b7f4f3",
      "--legend-color": "#b7f4f3",
      "--page-color": "#dedbdbff",
      "--page-color1": "#fff",
      "--svg-color1":"#00324d"
    },

    lightOrange: {
      "--background-color": "#fa6821",
      "--button-color": "#fa6821",
      "--svg-color": "#fa9868",
      "--component-background-color": "#FEEEE5",
      "--authbox-color": "#fff",
      "--menu-item-color": "#ffff",
      "--text-color": "rgba(0, 0, 0,0.8)",
      "--topbar-background":
        "linear-gradient(to right, #f0f0f0, #fa9868, #F6921E, #fa6821)",
      "--card-background": "linear-gradient(to bottom, #fa6821, #fa9868)",
      "--logo-background-image": `url(${logo})`,
      "--line-color": "#E2E8F0",
      "--page-color": "#fa9868",
    },

    dark: {
      "--background-color": "#111",
      "--button-color": "#333",
      "--svg-color": "#0b4f50ff",
      "--sidebar-color": "#292929ff",
      "--sidebar-hover-color": "#8a8a8aff",
      "--component-background-color": "#1a1a1a",
      "--authbox-color": "#1e1e1e",
      "--menu-item-color": "#fff",
      "--text-color": "#ffffff",
      "--topbar-background": "linear-gradient(to right, #111, #222, #333)",
      "--card-background": "#292929ff",
      "--logo-background-image": `url(${logo})`,
      "--line-color": "#858383ff",
      "--legend-color": "#b7f4f3",
      "--page-color": "#0d0d0d",
      "--page-color1": "#333",
      "--svg-color1":"#a6c7d9",
    },
  };

  const [theme, setTheme] = useState<ThemeName>(() => {
    const storedTheme = localStorage.getItem("theme") as ThemeName | null;
    return storedTheme && themes[storedTheme] ? storedTheme : "lightBlue";
  });

  /* ------------------ Apply Styles ------------------ */
  useEffect(() => {
    const root = document.documentElement;
    const themeStyles = themes[theme];

    Object.entries(themeStyles).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    window.dispatchEvent(new Event("themeChanged"));
  }, [theme]);

  /* ------------------ Theme Actions ------------------ */

  const handleThemeChange = (selectedTheme: ThemeName) => {
    setTheme(selectedTheme);
    localStorage.setItem("theme", selectedTheme);
  };

  const applyBackendTheme = (backendTheme: ThemeName) => {
    if (themes[backendTheme]) {
      setTheme(backendTheme);
      localStorage.setItem("theme", backendTheme);
    }
  };

  return (
    <ThemeContext.Provider
      value={{ theme, handleThemeChange, applyBackendTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
};
